import { OfferedProjectType } from "../../../../clients/graphqlTypes.js";
import {
  parseMatrixCellKey,
  prefillBufferFromVerticals,
  type TaxonomyIndex,
} from "../offeredServices/catalogueModel.js";
import {
  initialOnboardingFlowWizardState,
  type OfferedServicesCommitPath,
  type OfferedServicesPayload,
  type OnboardingFlowWizardAction,
  type OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";

function toggleId(arr: string[], id: string): string[] {
  return arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];
}

function toggleProjectType(
  arr: OfferedProjectType[],
  projectType: OfferedProjectType
): OfferedProjectType[] {
  return arr.includes(projectType)
    ? arr.filter(x => x !== projectType)
    : [...arr, projectType];
}

export function onboardingFlowWizardReducer(
  state: OnboardingFlowWizardState,
  action: OnboardingFlowWizardAction
): OnboardingFlowWizardState {
  switch (action.type) {
    case "toggleVertical":
      // Changing verticals invalidates the matrix prefill (declared rows +
      // cells were derived from the old vertical set). Clear matrix state so
      // re-entry to the matrix stage runs prefill against the new verticals.
      return {
        ...state,
        selectedVerticals: toggleId(state.selectedVerticals, action.categoryId),
        matrixDeclaredCategories: [],
        matrixSelections: [],
      };

    case "toggleProjectType":
      // Same reasoning as toggleVertical — changing project types invalidates
      // the matrix prefill's cell cross-product.
      return {
        ...state,
        selectedProjectTypes: toggleProjectType(
          state.selectedProjectTypes,
          action.projectType
        ),
        matrixDeclaredCategories: [],
        matrixSelections: [],
      };

    case "setApplyToAll":
      // Picking "yes" makes the specify-mode + matrix branch irrelevant.
      // Clear those so a user who backtracks doesn't carry stale state forward.
      if (action.value === "yes") {
        return {
          ...state,
          applyToAll: "yes",
          specifyMode: "unset",
          matrixDeclaredCategories: [],
          matrixSelections: [],
        };
      }
      return { ...state, applyToAll: "no" };

    case "setSpecifyMode":
      // Picking "later" makes any prior matrix state moot — clear it.
      if (action.value === "later") {
        return {
          ...state,
          specifyMode: "later",
          matrixDeclaredCategories: [],
          matrixSelections: [],
        };
      }
      return { ...state, specifyMode: "now" };

    case "setMatrixBuffer":
      // The cascade grid owns the full buffer; store it verbatim (including
      // any session-memory cells for undeclared categories). The commit
      // selector filters selections to declared rows.
      return {
        ...state,
        matrixDeclaredCategories: [...action.declaredCategories],
        matrixSelections: [...action.selections],
      };

    case "reset":
      return initialOnboardingFlowWizardState;
  }
}

// Path → payload selector. Maps the wizard state + the chosen commit path
// to the exact { categories, services } shape consumed by
// setOrganisationOfferedServices.
export function selectOfferedServicesPayload(
  state: OnboardingFlowWizardState,
  path: OfferedServicesCommitPath,
  index: TaxonomyIndex
): OfferedServicesPayload {
  switch (path) {
    case "skip-project-types":
    case "specify-later":
    case "skip-matrix":
      return { categories: state.selectedVerticals, services: [] };

    case "apply-to-all-yes": {
      // Cascade the picked project types down each vertical's subtree onto its
      // leaves — identical to accepting the specify-matrix prefill and
      // committing. A flat vertical×projectType cross-product would write
      // services on non-leaf verticals, which the leaf-only catalogue never
      // renders (they would appear unapplied).
      const buffer = prefillBufferFromVerticals(
        index,
        state.selectedVerticals,
        state.selectedProjectTypes
      );
      return {
        categories: [...buffer.declared],
        services: [...buffer.selections].map(parseMatrixCellKey),
      };
    }

    case "specify-now-commit": {
      // `categories` is the declared-row set from the cascade grid (already
      // includes forced-active ancestors). Selections may retain session-memory
      // cells for undeclared categories, so filter to declared rows — only
      // active-row services are committed.
      const declaredSet = new Set(state.matrixDeclaredCategories);
      const services = state.matrixSelections.filter(s =>
        declaredSet.has(s.categoryId)
      );
      const categories = Array.from(
        new Set([
          ...state.matrixDeclaredCategories,
          ...services.map(s => s.categoryId),
        ])
      );
      return {
        categories,
        services: services.map(s => ({
          categoryId: s.categoryId,
          projectType: s.projectType,
        })),
      };
    }
  }
}
